import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget.
 *
 * Loads the api.js script once, renders the widget into a scoped div,
 * and exposes the current token via `getToken()` on the DOM node (so
 * callers can read it at submit time without a controlled state).
 *
 * The widget writes the token into a hidden `cf-turnstile-response`
 * input that Cloudflare's script auto-injects; we also expose it via
 * `data-token` for convenience.
 *
 * Include `data-action="turnstile-spin-v2"` per Spin skill telemetry
 * marker (account-level aggregate; never per-user).
 */
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Cloudflare's api.js is cross-origin, so any errors it emits surface as
 * `Script error.` with no stack/source. The React dev overlay treats those
 * as "uncaught runtime errors" and takes over the page. Install a global
 * capture-phase handler ONCE that stops propagation for those opaque
 * cross-origin errors so the widget can render in peace.
 * No-op in production builds (React doesn't inject the overlay).
 */
let _crossOriginErrorSwallowerInstalled = false;
function installCrossOriginErrorSwallower() {
  if (_crossOriginErrorSwallowerInstalled || typeof window === "undefined") return;
  _crossOriginErrorSwallowerInstalled = true;
  const handler = (e) => {
    // Opaque cross-origin errors arrive with no filename and message "Script error."
    if (!e) return;
    const opaque = (e.message === "Script error." || e.message === "Script error")
      && !e.filename && !e.error;
    if (opaque) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  };
  window.addEventListener("error", handler, true);
}

function ensureScript() {
  if (typeof document === "undefined") return;
  installCrossOriginErrorSwallower();
  if (document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`)) return;
  const s = document.createElement("script");
  s.src = TURNSTILE_SCRIPT;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export default function Turnstile({ onToken, className = "", theme = "light" }) {
  const containerRef = useRef(null);
  const sitekey = process.env.REACT_APP_TURNSTILE_SITEKEY;

  useEffect(() => {
    ensureScript();
    // The Turnstile script auto-renders any `.cf-turnstile` div it finds
    // when it loads and on subsequent DOM mutations. If `onToken` is set,
    // wire the callback via a name we register on window.
    if (onToken && typeof window !== "undefined") {
      const cbName = `turnstileCb_${Math.random().toString(36).slice(2, 10)}`;
      window[cbName] = (token) => onToken(token);
      if (containerRef.current) {
        containerRef.current.setAttribute("data-callback", cbName);
      }
      return () => { delete window[cbName]; };
    }
    return undefined;
  }, [onToken]);

  if (!sitekey) {
    // In dev environments where the site key is missing, render nothing
    // rather than a broken widget. The backend verify() no-ops when
    // TURNSTILE_SECRET is also unset, so submission still works.
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`cf-turnstile ${className}`.trim()}
      data-sitekey={sitekey}
      data-action="turnstile-spin-v2"
      data-theme={theme}
      data-testid="turnstile-widget"
    />
  );
}

/** Read the token from the nearest Turnstile widget inside a form-like element. */
export function readTurnstileToken(scope) {
  if (!scope) return "";
  const input = scope.querySelector('input[name="cf-turnstile-response"]');
  return input ? input.value : "";
}
